import React, { useEffect } from 'react';
import { DEFAULT_ACCOUNT_NAMES_BY_POOL_ID } from 'src/constants/constants';
import { MAIN_POOL_ID } from '@velocity-exchange/common';
import { isAmplifyAccountName } from 'src/utils/amplify';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { CollateralSourceSelector } from 'src/components/Inputs/CollateralSourceSelector';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { AccountData } from 'src/stores/useDriftAccountsStore';
import InputUi from 'src/components/primitives/Input/InputUi';
import { WithChildren } from 'src/utils/UtilityTypes';

interface SubAccountSelectorProps {
	selectedTargetAccountKey: string;
	selectedPoolId: number;
	handleTargetAccountChange: (newAccountKey: string) => void;
	mode: 'deposit' | 'withdraw';
	newSubaccount?:
		| {
				isNewAccount: false;
		  }
		| {
				isNewAccount: true;
				defaultName: string;
		  };
	includeDelegates?: boolean;
	accountLabel?: string;
	/** Optional filter for which accounts to show */
	filterAccount?: (account: AccountData) => boolean;
}

const SubAccountSelector = ({
	selectedTargetAccountKey,
	selectedPoolId,
	handleTargetAccountChange,
	mode,
	newSubaccount,
	includeDelegates = false,
	accountLabel,
	filterAccount,
}: SubAccountSelectorProps) => {
	const allSubaccounts = useTickedAllSubAccounts();
	const availableSubaccounts = filterAccount
		? allSubaccounts.filter(filterAccount)
		: allSubaccounts;

	// When filtered, reset selection if current account is no longer in the list
	useEffect(() => {
		if (
			!filterAccount ||
			availableSubaccounts.length === 0 ||
			availableSubaccounts.some((a) => a.userKey === selectedTargetAccountKey)
		)
			return;
		handleTargetAccountChange(availableSubaccounts[0].userKey);
	}, [
		filterAccount,
		availableSubaccounts,
		selectedTargetAccountKey,
		handleTargetAccountChange,
	]);

	const currentUserName = useDriftAccountStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']?.name ?? ''
	);

	const isDepositingToIsolatedPool = selectedPoolId !== MAIN_POOL_ID;
	const isAmplifyAccount = isAmplifyAccountName(currentUserName);

	const displayNewSubaccountName =
		mode === 'deposit' &&
		newSubaccount?.isNewAccount &&
		availableSubaccounts.length > 0;

	const displaySubaccountSelector =
		mode === 'deposit'
			? !newSubaccount?.isNewAccount && availableSubaccounts.length > 1
			: availableSubaccounts.length > 1;

	const labelText =
		accountLabel ?? (mode === 'deposit' ? 'Deposit to' : 'Withdraw from');

	return (
		<div className="flex flex-col gap-1">
			{displayNewSubaccountName && newSubaccount?.isNewAccount && (
				<MockSelector label={labelText}>
					{newSubaccount.defaultName}
				</MockSelector>
			)}

			{displaySubaccountSelector && (
				<CollateralSourceSelector
					label={labelText}
					onChange={(newAccountKey) => {
						handleTargetAccountChange(newAccountKey);
					}}
					includeDelegates={includeDelegates}
					options={availableSubaccounts}
					initialSelection={selectedTargetAccountKey}
				/>
			)}

			{!displaySubaccountSelector && !displayNewSubaccountName && (
				<MockSelector label={labelText}>
					{isAmplifyAccount
						? `Amplify ${currentUserName.replace('Amplify ', '')}`
						: currentUserName ||
							DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[selectedPoolId]}
				</MockSelector>
			)}

			{mode === 'deposit' && isDepositingToIsolatedPool && (
				<InlineAlert
					variant="warning"
					title="You are depositing to an Isolated Pool. Deposits can only be used as collateral for borrowing within this pool."
					className="mt-2xs"
				/>
			)}
		</div>
	);
};

interface MockSelectorProps extends WithChildren {
	label: string;
}

function MockSelector({ label, children }: MockSelectorProps) {
	return (
		<InputUi.Container disabled>
			<InputUi.InsetLabel size="sm">{label}</InputUi.InsetLabel>
			<InputUi.Value size="sm" className="text-right">
				{children}
			</InputUi.Value>
		</InputUi.Container>
	);
}

export default SubAccountSelector;
